import { AdminPermission as GraphQLAdminPermission } from '../../generated/graphql';

export interface AdminUser {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  role_id?: string;
  permissions: string[];
  is_super_admin: boolean;
  is_active: boolean;
  phone_number?: string;
  avatar_url?: string;
  last_login_at?: string;
  last_login_ip?: string;
  created_at: string;
  doctor_id?: string;
  doctor?: {
    id: string;
    first_name_en: string;
    last_name_en: string;
    first_name_zh_hk: string;
    last_name_zh_hk: string;
    specialization: string;
    license_number: string;
  };
  clinic_id?: string;
  clinic?: {
    id: string;
    name_en: string;
    name_zh_hk: string;
    address?: string;
    region?: string;
    district?: string;
    phone_number?: string;
    email?: string;
    internal_name?: string;
    status?: string;
  };
  userRole?: {
    id: string;
    name: string;
    description: string;
    is_system_role: boolean;
    is_active: boolean;
    permissions: Array<{
      role_id: string;
      permission: string;
    }>;
  };
}

export interface AdminRole {
  id: string;
  name: string;
  description: string;
  is_system_role: boolean;
  is_active: boolean;
  user_count: number;
  permissions: Array<{
    role_id: string;
    permission: string;
  }>;
}

export interface AdminPermission {
  id: string;
  permission: string;
  name: string;
  description: string;
  category: string;
  is_active: boolean;
}

// Permission metadata mapping since the API only returns permission enum values
export const PERMISSION_METADATA: Record<string, { name: string; description: string; category: string }> = {
  VIEW_USERS: { name: 'VIEW_USERS', description: 'View Users', category: 'userManagement' },
  CREATE_USERS: { name: 'CREATE_USERS', description: 'Create Users', category: 'userManagement' },
  UPDATE_USERS: { name: 'UPDATE_USERS', description: 'Update Users', category: 'userManagement' },
  DELETE_USERS: { name: 'DELETE_USERS', description: 'Delete Users', category: 'userManagement' },
  VIEW_DOCTORS: { name: 'VIEW_DOCTORS', description: 'View Doctors', category: 'doctorManagement' },
  CREATE_DOCTORS: { name: 'CREATE_DOCTORS', description: 'Create Doctors', category: 'doctorManagement' },
  UPDATE_DOCTORS: { name: 'UPDATE_DOCTORS', description: 'Update Doctors', category: 'doctorManagement' },
  DELETE_DOCTORS: { name: 'DELETE_DOCTORS', description: 'Delete Doctors', category: 'doctorManagement' },
  VIEW_CLINICS: { name: 'VIEW_CLINICS', description: 'View Clinics', category: 'clinicManagement' },
  CREATE_CLINICS: { name: 'CREATE_CLINICS', description: 'Create Clinics', category: 'clinicManagement' },
  UPDATE_CLINICS: { name: 'UPDATE_CLINICS', description: 'Update Clinics', category: 'clinicManagement' },
  DELETE_CLINICS: { name: 'DELETE_CLINICS', description: 'Delete Clinics', category: 'clinicManagement' },
  VIEW_PROVIDERS: { name: 'VIEW_PROVIDERS', description: 'View Providers', category: 'providerManagement' },
  CREATE_PROVIDERS: { name: 'CREATE_PROVIDERS', description: 'Create Providers', category: 'providerManagement' },
  UPDATE_PROVIDERS: { name: 'UPDATE_PROVIDERS', description: 'Update Providers', category: 'providerManagement' },
  DELETE_PROVIDERS: { name: 'DELETE_PROVIDERS', description: 'Delete Providers', category: 'providerManagement' },
  VIEW_APPOINTMENTS: { name: 'VIEW_APPOINTMENTS', description: 'View Appointments', category: 'appointmentManagement' },
  CREATE_APPOINTMENTS: { name: 'CREATE_APPOINTMENTS', description: 'Create Appointments', category: 'appointmentManagement' },
  UPDATE_APPOINTMENTS: { name: 'UPDATE_APPOINTMENTS', description: 'Update Appointments', category: 'appointmentManagement' },
  DELETE_APPOINTMENTS: { name: 'DELETE_APPOINTMENTS', description: 'Delete Appointments', category: 'appointmentManagement' },
  VIEW_PATIENTS: { name: 'VIEW_PATIENTS', description: 'View Patients', category: 'patientManagement' },
  CREATE_PATIENTS: { name: 'CREATE_PATIENTS', description: 'Create Patients', category: 'patientManagement' },
  UPDATE_PATIENTS: { name: 'UPDATE_PATIENTS', description: 'Update Patients', category: 'patientManagement' },
  DELETE_PATIENTS: { name: 'DELETE_PATIENTS', description: 'Delete Patients', category: 'patientManagement' },
  VIEW_SYSTEM: { name: 'VIEW_SYSTEM', description: 'View System', category: 'systemManagement' },
  MANAGE_ADMINS: { name: 'MANAGE_ADMINS', description: 'Manage Admins', category: 'systemManagement' },
  VIEW_LOGS: { name: 'VIEW_LOGS', description: 'View Logs', category: 'systemManagement' },
  MANAGE_SETTINGS: { name: 'MANAGE_SETTINGS', description: 'Manage Settings', category: 'systemManagement' },
  VIEW_MY_APPOINTMENTS: { name: 'VIEW_MY_APPOINTMENTS', description: 'View My Appointments', category: 'appointmentManagement' },
  MANAGE_MY_APPOINTMENTS: { name: 'MANAGE_MY_APPOINTMENTS', description: 'Manage My Appointments', category: 'appointmentManagement' }
};

// Convert database role value to GraphQL enum key
export const convertRoleValueToKey = (roleValue: string): string => {
  const roleValueToKey: Record<string, string> = {
    'super_admin': 'SUPER_ADMIN',
    'admin': 'ADMIN',
    'manager': 'MANAGER',
    'operator': 'OPERATOR',
    'nurse': 'NURSE',
    'doctor': 'DOCTOR',
  };
  return roleValueToKey[roleValue] || roleValue;
};

// Convert GraphQL enum key to database role value
export const convertRoleKeyToValue = (roleKey: string): string => {
  const roleKeyToValue: Record<string, string> = {
    'SUPER_ADMIN': 'super_admin',
    'ADMIN': 'admin',
    'MANAGER': 'manager',
    'OPERATOR': 'operator',
    'NURSE': 'nurse',
    'DOCTOR': 'doctor',
  };
  return roleKeyToValue[roleKey] || roleKey;
};
